import React, { useEffect, useRef, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import styled from 'styled-components';

import { Button, Dropdown, DropdownElement, Input, Modal, ModalSize, Toggle } from '@column/column-ui-kit';

import { AddressField, NotificationList, RenderFields } from '~/components';
import { RoutingNumberField } from '~/components/RoutingNumberField';
import { useAddress } from '~/hooks';
import { useFormErrorHandler } from '~/hooks/useFormErrorHandler';
import { COUNTERPARTY_ACCOUNT_TYPE_OPTIONS, coerceCounterpartyAccountType } from '~/lib/counterparties/accountType';
import { CounterpartyRepository, CreateCounterparty } from '~/repositories';
import { useNotificationStore } from '~/stores/Notification';
import { Divider, FormText } from '~/styles';

export const Form = styled.form`
  display: grid;
  grid-gap: 24px;
`;

const StyledNotificationList = styled(NotificationList)`
  margin: 16px 0 0 0;
`;

interface CounterpartyForm extends CreateCounterparty {}

export interface ModalCounterpartyProps {
  isOpen: boolean;
  onClose: () => void;
  callback?: (counterparty: any) => void;
  type?: string;
}

export const ModalCounterparty: React.FC<ModalCounterpartyProps> = ({ isOpen, onClose, callback, type: modalType }) => {
  const addDangerNotification = useNotificationStore((s) => s.addDangerNotification);
  const [type, setType] = useState<string>(modalType || '');
  const [beneficiaryAddressSearchValue, setBeneficiaryAddressSearchValue] = useState('');
  const { states, countries } = useAddress({ types: ['state', 'country'] });
  const stateRef = useRef<DropdownElement>(null);

  const formMethods = useForm<CounterpartyForm>({
    defaultValues: {
      description: '',
      routingNumber: '',
      routingNumberType: undefined,
      accountNumber: '',
      accountType: 'checking',
      wireDrawdownAllowed: false,
      wire: {
        beneficiaryName: '',
        beneficiaryEmail: '',
        beneficiaryPhone: '',
        beneficiaryLegalId: '',
        beneficiaryType: undefined,
        localBankCode: '',
        localAccountNumber: '',
        beneficiaryAddress: {
          line1: '',
          line2: '',
          city: '',
          countryCode: '',
          postalCode: '',
          state: '',
        },
      },
    },
  });

  const { handleSubmit, watch, reset, setValue } = formMethods;
  const { handleSubmitErrors } = useFormErrorHandler();

  const onSubmit = (data: CounterpartyForm) => {
    const payload = { ...data, accountType: coerceCounterpartyAccountType(data.accountType) ?? 'checking' };
    if (type !== 'wire' && type !== 'realtime') {
      delete payload.wire;
    }

    CounterpartyRepository.create(payload)
      .then((response) => {
        if (callback) {
          callback(response);
        }
        onClose();
      })
      .catch((error) => {
        addDangerNotification({
          content: error.message,
          display: 'popup',
        });
      });
  };

  const handleClose = (e: React.FormEvent) => {
    e.preventDefault();
    onClose();
  };

  // Initialize form with type when modal opens
  useEffect(() => {
    if (isOpen) {
      reset();
      setType(modalType || '');
      setBeneficiaryAddressSearchValue('');
    }
  }, [isOpen, reset, modalType]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size={type === 'wire' ? ModalSize.Large : ModalSize.Medium}
      withClose={false}
    >
      <Modal.Header title="New Counterparty">
        <Modal.Close variant="subtle" size="small" onClick={handleClose} />
      </Modal.Header>

      <Modal.Content align="left">
        <StyledNotificationList display="popup" />
        <FormProvider {...formMethods}>
          <Form onSubmit={handleSubmit(onSubmit, handleSubmitErrors)}>
            {RenderFields<CounterpartyForm>({
              sections: [
                {
                  variant: 'isCompact',
                  fields: [
                    {
                      id: 'description',
                      label: 'Description',
                      children: ({ value, onChange }) => (
                        <Input value={String(value)} onChange={onChange} placeholder="Description" />
                      ),
                    },
                    {
                      id: 'routingNumber',
                      label: 'Routing Number',
                      rules: { required: true },
                      children: ({ value, onChange }) => (
                        <RoutingNumberField routingNumber={String(value)} onRoutingNumberChange={onChange} />
                      ),
                    },
                    {
                      id: 'routingNumberType',
                      label: 'Routing Number Type',
                      children: ({ value, onChange }) => (
                        <Dropdown
                          fullWidth
                          active={String(value)}
                          onChange={onChange}
                          options={[
                            {
                              label: 'ABA',
                              value: 'aba',
                            },
                            {
                              label: 'BIC',
                              value: 'bic',
                            },
                          ]}
                        />
                      ),
                    },
                    {
                      id: 'accountNumber',
                      label: 'Account Number',
                      rules: { required: true },
                      children: ({ value, onChange }) => (
                        <Input value={String(value)} onChange={onChange} placeholder="Account number" />
                      ),
                    },
                    {
                      id: 'accountType',
                      label: 'Account Type',
                      children: ({ value, onChange }) => (
                        <Dropdown
                          fullWidth
                          active={String(value ?? 'checking')}
                          onChange={onChange}
                          options={COUNTERPARTY_ACCOUNT_TYPE_OPTIONS}
                        />
                      ),
                    },
                    {
                      id: 'wire.beneficiaryName',
                      label: 'Beneficiary Name',
                      hide: type !== 'realtime' && type !== 'wire',
                      children: ({ value, onChange }) => (
                        <Input value={String(value)} onChange={onChange} placeholder="Beneficiary name" />
                      ),
                    },
                    {
                      id: 'wireDrawdownAllowed',
                      label: 'Wire Drawdown Allowed',
                      hide: type !== 'wire',
                      children: ({ value, onChange }) => (
                        <FormText>
                          <Toggle isChecked={!!value} onCheckedChange={onChange} />
                        </FormText>
                      ),
                    },
                    {
                      id: 'wire.beneficiaryEmail',
                      label: 'Beneficiary Email',
                      hide: type !== 'wire',
                      children: ({ value, onChange }) => (
                        <Input value={String(value)} onChange={onChange} placeholder="Beneficiary email" />
                      ),
                    },
                    {
                      id: 'wire.beneficiaryPhone',
                      label: 'Beneficiary Phone',
                      hide: type !== 'wire',
                      children: ({ value, onChange }) => (
                        <Input value={String(value)} onChange={onChange} placeholder="Beneficiary phone" />
                      ),
                    },
                    {
                      id: 'wire.beneficiaryLegalId',
                      label: 'Beneficiary Legal ID',
                      hide: type !== 'wire',
                      children: ({ value, onChange }) => (
                        <Input value={String(value)} onChange={onChange} placeholder="Beneficiary legal ID" />
                      ),
                    },
                    {
                      id: 'wire.beneficiaryType',
                      label: 'Beneficiary Type',
                      hide: type !== 'wire',
                      children: ({ value, onChange }) => (
                        <Dropdown
                          fullWidth
                          active={String(value)}
                          onChange={onChange}
                          options={[
                            {
                              label: 'Business',
                              value: 'business',
                            },
                            {
                              label: 'Nonprofit',
                              value: 'non_profit',
                            },
                            {
                              label: 'Individual',
                              value: 'individual',
                            },
                            {
                              label: 'Sole proprietor',
                              value: 'sole_proprietor',
                            },
                          ]}
                        />
                      ),
                    },
                    {
                      id: 'wire.localBankCode',
                      label: 'Local Bank Code',
                      hide: type !== 'wire',
                      children: ({ value, onChange }) => (
                        <Input value={String(value)} onChange={onChange} placeholder="Local bank code" />
                      ),
                    },
                    {
                      id: 'wire.localAccountNumber',
                      label: 'Local Account Number',
                      hide: type !== 'wire',
                      children: ({ value, onChange }) => (
                        <Input value={String(value)} onChange={onChange} placeholder="Local account number" />
                      ),
                    },
                    {
                      hide: type !== 'wire',
                      content: <Divider fullWidth>Beneficiary Address</Divider>,
                    },
                    {
                      id: 'wire.beneficiaryAddress',
                      label: 'Address Lookup',
                      hide: type !== 'wire',
                      noStyle: true,
                      children: () => (
                        <AddressField
                          onChange={(a) => {
                            setValue('wire.beneficiaryAddress.line1', a.line1 ?? '');
                            setValue('wire.beneficiaryAddress.line2', a.line2 ?? '');
                            setValue('wire.beneficiaryAddress.city', a.city ?? '');
                            setValue('wire.beneficiaryAddress.state', a.state ?? '');
                            setValue('wire.beneficiaryAddress.postalCode', a.postalCode ?? '');
                            setValue('wire.beneficiaryAddress.countryCode', a.countryCode ?? '');
                            setBeneficiaryAddressSearchValue('');
                            if (!a.state) {
                              stateRef.current?.open();
                            }
                          }}
                          value={beneficiaryAddressSearchValue}
                          onValueChange={setBeneficiaryAddressSearchValue}
                        />
                      ),
                    },
                    {
                      hide: type !== 'wire',
                      content: <Divider fullWidth>or</Divider>,
                    },
                    {
                      id: 'wire.beneficiaryAddress.line1',
                      label: 'Line 1',
                      hide: type !== 'wire',
                      children: ({ value, onChange }) => (
                        <Input value={String(value)} onChange={onChange} placeholder="Line 1" />
                      ),
                    },
                    {
                      id: 'wire.beneficiaryAddress.line2',
                      label: 'Line 2',
                      hide: type !== 'wire',
                      children: ({ value, onChange }) => (
                        <Input value={String(value)} onChange={onChange} placeholder="Line 2" />
                      ),
                    },
                    {
                      id: 'wire.beneficiaryAddress.city',
                      label: 'City',
                      hide: type !== 'wire',
                      children: ({ value, onChange }) => (
                        <Input value={String(value)} onChange={onChange} placeholder="City" />
                      ),
                    },
                    {
                      id: 'wire.beneficiaryAddress.countryCode',
                      label: 'Country',
                      hide: type !== 'wire',
                      children: ({ value, onChange }) => (
                        <Dropdown
                          fullWidth
                          active={String(value)}
                          onChange={onChange}
                          options={countries.map((c) => ({
                            label: c.name,
                            value: c.code,
                          }))}
                          search
                        />
                      ),
                    },
                    {
                      id: 'wire.beneficiaryAddress.postalCode',
                      label: 'Postal Code',
                      hide: type !== 'wire',
                      children: ({ value, onChange }) => (
                        <Input value={String(value)} onChange={onChange} placeholder="Postal code" />
                      ),
                    },
                    {
                      id: 'wire.beneficiaryAddress.state',
                      label: 'State',
                      hide: type !== 'wire',
                      children: ({ value, onChange }) => (
                        <Dropdown
                          ref={stateRef}
                          fullWidth
                          active={String(value)}
                          onChange={onChange}
                          options={
                            watch('wire.beneficiaryAddress.countryCode')
                              ? states
                                  .filter((s) => s.country_code === watch('wire.beneficiaryAddress.countryCode'))
                                  .map((s) => ({
                                    label: `${s.name} (${s.state_code})`,
                                    value: s.state_code,
                                  }))
                              : []
                          }
                          search
                        />
                      ),
                    },
                  ],
                },
              ],
            })}
          </Form>
        </FormProvider>
      </Modal.Content>

      <Modal.Footer>
        <Button variant="secondary" size="small" onClick={onClose}>
          Cancel
        </Button>
        <Button
          size="small"
          onClick={() => {
            handleSubmit(onSubmit, handleSubmitErrors)();
          }}
        >
          Create &amp; Select
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
